import type {AdminConfig, Language} from '../../types/types.ts';
import {loadAdminConfig, saveAdminConfig} from '../services/content.ts';
import './admin.css';

export async function renderAdminPage(appEl: HTMLElement) {
	appEl.innerHTML = `
<div class="admin-page">
	<h1>Admin Settings</h1>
	<div class="admin-loading">Loading configuration...</div>
</div>`;

	const config = await loadAdminConfig();
	if (!config) {
		appEl.innerHTML = `
<div class="admin-page">
	<h1>Admin Settings</h1>
	<div class="admin-error">Failed to load configuration. Please try again.</div>
</div>`;
		return;
	}

	renderForm(appEl, config);
}

function renderForm(appEl: HTMLElement, config: AdminConfig) {
	const languages: {code: Language; name: string}[] = [
		{code: 'en', name: 'English'},
		{code: 'no', name: 'Norsk'},
		{code: 'da', name: 'Dansk'},
		{code: 'de', name: 'Deutsch'}
	];

	appEl.innerHTML = `
<div class="admin-page">
	<h1>Admin Settings</h1>

	<section class="admin-section">
		<h2>General Settings</h2>
		<form class="admin-form">
			<div class="form-group">
				<label for="language">Language</label>
				<select id="language" name="language">
					${languages.map((l) => `<option value="${l.code}" ${config.language === l.code ? 'selected' : ''}>${l.name}</option>`).join('')}
				</select>
				<p class="help-text">Language for reader-facing UI elements.</p>
			</div>

			<div class="form-group checkbox-group">
				<label>
					<input type="checkbox" id="autoSummary" name="autoSummary" ${config.autoSummary ? 'checked' : ''}>
					Auto-generate summary article
				</label>
				<p class="help-text">When enabled, an AI-generated summary will be added as the first article. Disable this if you create summary articles manually.</p>
			</div>

			<div class="form-group">
				<label for="customPrompt">Summary AI prompt</label>
				<textarea id="customPrompt" name="customPrompt" rows="8" placeholder="Leave empty to use default prompt...">${config.customPrompt || ''}</textarea>
				<p class="help-text prompt-status">${config.customPrompt ? 'Using custom prompt.' : 'Using default prompt.'} <a href="#" class="load-default-link">Load default prompt into editor</a></p>
			</div>

			<div class="form-actions">
				<button type="submit" class="button primary">Save Settings</button>
				<span class="save-status"></span>
			</div>
		</form>
	</section>
</div>`;

	const form = appEl.querySelector('.admin-form') as HTMLFormElement;
	const saveStatus = appEl.querySelector('.save-status') as HTMLElement;
	const customPromptTextarea = appEl.querySelector('#customPrompt') as HTMLTextAreaElement;
	const loadDefaultLink = appEl.querySelector('.load-default-link') as HTMLAnchorElement;

	loadDefaultLink.addEventListener('click', (e) => {
		e.preventDefault();
		customPromptTextarea.value = config.defaultPrompt;
		customPromptTextarea.focus();
	});

	form.addEventListener('submit', async (e) => {
		e.preventDefault();
		saveStatus.textContent = 'Saving...';
		saveStatus.className = 'save-status saving';

		const formData = new FormData(form);
		const autoSummaryCheckbox = form.querySelector('#autoSummary') as HTMLInputElement;
		const success = await saveAdminConfig({
			language: formData.get('language') as Language,
			customPrompt: formData.get('customPrompt') as string,
			autoSummary: autoSummaryCheckbox.checked
		});

		if (success) {
			saveStatus.textContent = 'Saved!';
			saveStatus.className = 'save-status success';
		} else {
			saveStatus.textContent = 'Failed to save';
			saveStatus.className = 'save-status error';
		}

		setTimeout(() => {
			saveStatus.textContent = '';
			saveStatus.className = 'save-status';
		}, 3000);
	});
}
